import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Receipt, Search, Wallet, ArrowUpRight, ArrowDownLeft, ShoppingCart, ArrowLeftRight, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { isSupportedCrypto } from "@/lib/supported-symbols";

type Kind = "purchase" | "deposit" | "withdrawal" | "internal_in" | "internal_out";

interface UnifiedRow {
  id: string;
  kind: Kind;
  crypto_symbol: string;
  amount: number;
  status: string;
  created_at: string;
  transaction_number?: string | null;
  tx_hash?: string | null;
  counterparty?: string | null;
  total_usd?: number | null;
  network?: string | null;
}

const KIND_META: Record<Kind, { label: string; icon: any; color: string }> = {
  purchase:    { label: "شراء",          icon: ShoppingCart, color: "text-purple-500" },
  deposit:     { label: "استقبال",       icon: ArrowDownLeft, color: "text-green-500" },
  withdrawal:  { label: "إرسال خارجي",   icon: ArrowUpRight,  color: "text-red-500" },
  internal_in: { label: "استلام داخلي",  icon: ArrowDownLeft, color: "text-green-500" },
  internal_out:{ label: "إرسال داخلي",   icon: ArrowUpRight,  color: "text-orange-500" },
};

const STATUS_LABEL: Record<string, string> = {
  approved: "تمت", completed: "تمت", success: "تمت", sent: "تمت", confirmed: "تمت",
  pending: "قيد المعالجة", processing: "قيد المعالجة", queued: "قيد المعالجة",
  rejected: "مرفوضة", failed: "فشلت",
};

const POLYGON_NETWORK_ALIASES = new Set(["POLYGON (POS)", "POLYGON", "MATIC", "POL", "0X89", "137"]);
const isPolygonNetwork = (network?: string | null) => POLYGON_NETWORK_ALIASES.has(String(network || "").trim().toUpperCase());

const Transactions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rows, setRows] = useState<UnifiedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let firstLoad = true;

    const fetchRows = async (silent = false) => {
      if (!silent) setLoading(true);
      const [tx, dep, wd, itr] = await Promise.all([
        supabase.from("transactions").select("id,crypto_symbol,amount,status,created_at,transaction_number,total_usd").eq("user_id", user.id),
        supabase.from("deposit_requests").select("id,crypto_symbol,network,amount,status,created_at,tx_hash,transaction_number").eq("user_id", user.id),
        supabase.from("withdrawal_requests").select("id,crypto_symbol,network,amount,status,created_at,tx_hash,transaction_number,wallet_address").eq("user_id", user.id),
        supabase.from("internal_transfers").select("id,sender_id,recipient_id,crypto_symbol,amount,transaction_number,sender_name,recipient_name,source_type,created_at").or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`),
      ]);
      if (cancelled) return;
      const unified: UnifiedRow[] = [];
      (tx.data || [])
        .filter((r: any) => isSupportedCrypto(String(r.crypto_symbol || "")))
        .forEach((r: any) => unified.push({
        id: `p-${r.id}`, kind: "purchase", crypto_symbol: String(r.crypto_symbol || "").toUpperCase(), amount: Number(r.amount),
        status: r.status, created_at: r.created_at, transaction_number: r.transaction_number, total_usd: Number(r.total_usd),
      }));
      (dep.data || [])
        .filter((r: any) => isSupportedCrypto(String(r.crypto_symbol || "")) && isPolygonNetwork(r.network))
        .forEach((r: any) => unified.push({
        id: `d-${r.id}`, kind: "deposit", crypto_symbol: String(r.crypto_symbol || "").toUpperCase(), amount: Number(r.amount),
        status: r.status, created_at: r.created_at, tx_hash: r.tx_hash, transaction_number: r.transaction_number, network: r.network,
      }));
      (wd.data || [])
        .filter((r: any) => isSupportedCrypto(String(r.crypto_symbol || "")) && isPolygonNetwork(r.network))
        .forEach((r: any) => unified.push({
        id: `w-${r.id}`, kind: "withdrawal", crypto_symbol: String(r.crypto_symbol || "").toUpperCase(), amount: Number(r.amount),
        status: r.status, created_at: r.created_at, tx_hash: r.tx_hash, transaction_number: r.transaction_number, counterparty: r.wallet_address, network: r.network,
      }));
      // Prefetch numeric_ids for internal-transfer counterparties
      const counterIds = new Set<string>();
      (itr.data || []).forEach((r: any) => {
        if (r.sender_id && r.sender_id !== user.id) counterIds.add(r.sender_id);
        if (r.recipient_id && r.recipient_id !== user.id) counterIds.add(r.recipient_id);
      });
      const idMap = new Map<string, { numeric_id: string | null; full_name: string | null; email: string }>();
      if (counterIds.size > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, numeric_id, full_name, email")
          .in("user_id", Array.from(counterIds));
        (profs || []).forEach((p: any) => idMap.set(p.user_id, { numeric_id: p.numeric_id, full_name: p.full_name, email: p.email }));
      }

      (itr.data || [])
        .filter((r: any) => isSupportedCrypto(String(r.crypto_symbol || "")))
        .forEach((r: any) => {
        const isSender = r.sender_id === user.id;
        const isPlatform = !r.sender_id || r.source_type === 'platform';
        const otherId = isSender ? r.recipient_id : r.sender_id;
        const otherProfile = otherId ? idMap.get(otherId) : null;
        const otherLabel = isSender
          ? (r.recipient_name || otherProfile?.full_name || otherProfile?.email || "مستقبل")
          : (isPlatform ? "من المنصة (أرباح)" : (r.sender_name || otherProfile?.full_name || otherProfile?.email || "مرسل"));
        const idPart = isPlatform && !isSender
          ? ""
          : (otherProfile?.numeric_id ? ` (ID: ${otherProfile.numeric_id})` : "");
        unified.push({
          id: `i-${r.id}`,
          kind: isSender ? "internal_out" : "internal_in",
          crypto_symbol: String(r.crypto_symbol || "").toUpperCase(),
          amount: Number(r.amount),
          status: "approved",
          created_at: r.created_at,
          transaction_number: r.transaction_number,
          counterparty: otherLabel + idPart,
        });
      });
      unified.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRows(unified);
      if (!silent) setLoading(false);
      firstLoad = false;
    };

    const refresh = () => fetchRows(!firstLoad);
    refresh();

    // Incoming crypto deposits may be created or confirmed by background jobs
    // after the page opens, so keep this transaction list live as well.
    const pollTimer = window.setInterval(() => fetchRows(true), 1000);
    const syncTimer = window.setInterval(() => {
      supabase.functions.invoke("sync-balances", { body: { userId: user.id } }).then(() => fetchRows(true));
    }, 30000);
    supabase.functions.invoke("sync-balances", { body: { userId: user.id } }).then(() => fetchRows(true));

    const channel = supabase
      .channel(`transactions-live-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "transactions", filter: `user_id=eq.${user.id}` }, () => fetchRows(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "deposit_requests", filter: `user_id=eq.${user.id}` }, () => fetchRows(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "withdrawal_requests", filter: `user_id=eq.${user.id}` }, () => fetchRows(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "internal_transfers", filter: `sender_id=eq.${user.id}` }, () => fetchRows(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "internal_transfers", filter: `recipient_id=eq.${user.id}` }, () => fetchRows(true))
      .subscribe();

    return () => {
      cancelled = true;
      window.clearInterval(pollTimer);
      window.clearInterval(syncTimer);
      supabase.removeChannel(channel);
    };
  }, [user]);

  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    if (!q) return true;
    return r.crypto_symbol.toLowerCase().includes(q)
      || (r.transaction_number || "").toLowerCase().includes(q)
      || (r.tx_hash || "").toLowerCase().includes(q)
      || (r.counterparty || "").toLowerCase().includes(q);
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="w-16 h-16 rounded-xl gradient-primary flex items-center justify-center animate-spin-slow">
          <Wallet className="w-8 h-8 text-primary-foreground" />
        </div>
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{t('transactions.title')}</h1>
            <p className="text-muted-foreground">جميع عملياتك: شراء، إرسال، استقبال، تحويلات داخلية</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="بحث بالرمز أو رقم العملية أو الـ hash"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-input border-border text-foreground pr-10"
            />
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="space-y-2">
            {filtered.map((r) => {
              const meta = KIND_META[r.kind];
              const Icon = meta.icon;
              const statusText = STATUS_LABEL[r.status] || r.status;
              const isDone = ["approved","completed","success","sent","confirmed"].includes(r.status);
              const isFailed = ["rejected","failed"].includes(r.status);
              return (
                <div key={r.id} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                  <div className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center ${meta.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{meta.label}</span>
                      <span className="text-sm text-muted-foreground">{r.crypto_symbol}</span>
                      <Badge variant={isDone ? "default" : isFailed ? "destructive" : "secondary"} className="text-[10px]">
                        {statusText}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate" dir="ltr">
                      {r.transaction_number || r.tx_hash || r.counterparty || new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono font-semibold ${r.kind === "withdrawal" || r.kind === "internal_out" ? "text-red-500" : "text-green-500"}`} dir="ltr">
                      {r.kind === "withdrawal" || r.kind === "internal_out" ? "-" : "+"}{r.amount}
                    </div>
                    {r.tx_hash && (
                      <a
                        href={`https://polygonscan.com/tx/${r.tx_hash}`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-primary flex items-center gap-1 justify-end mt-0.5"
                      >
                        <ExternalLink className="w-2.5 h-2.5" /> Polygonscan
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Receipt className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">{t('transactions.noTransactions')}</h3>
            <p className="text-muted-foreground text-center">
              {search ? t('transactions.noSearchResults') : t('transactions.noTransactionsHint')}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Transactions;
